import { Grid, html } from "gridjs";
import "gridjs/dist/theme/mermaid.css";
import { uiState } from "../../state/uiState.js";

let kisilerGrid = null;
let didBindGridEvents = false;

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function ellipsisCell(cell) {
    const text = cell === null || cell === undefined || cell === "" ? "—" : String(cell);
    const safe = escapeHtml(text);
    return html(`<span class="grid-cell-ellipsis" title="${safe}">${safe}</span>`);
}

export function initKisilerGridEvents() {
    if (didBindGridEvents) return;
    didBindGridEvents = true;

    document.body.addEventListener("change", (e) => {
        if (e.target?.matches(".kisiler-per-page")) {
            const value = Number(e.target.value);
            uiState.kisiler.paginationLimit = value;
            rerenderKisilerGridWithLastUrl(value);
        }
    });

    document.body.addEventListener("click", (e) => {
        const viewBtn = e.target.closest(".kisiler-grid-action-btn--view");
        const editBtn = e.target.closest(".kisiler-grid-action-btn--edit");
        const toggleBtn = e.target.closest(".kisiler-grid-action-btn--toggle");
        if (viewBtn) window.openKisilerOverviewDialog?.(viewBtn.dataset.tc);
        if (editBtn) window.openKisilerEditDialog?.(editBtn.dataset.tc);
        if (toggleBtn) window.openKisilerDeleteDialog?.(toggleBtn.dataset.tc, toggleBtn.dataset.label, toggleBtn.dataset.active === "1");
    });
}

export function renderKisilerGrid({ url, paginationLimit = 10 }) {
    uiState.kisiler.lastSearchUrl = url;
    uiState.kisiler.paginationLimit = paginationLimit;

    if (kisilerGrid) {
        kisilerGrid.destroy();
        kisilerGrid = null;
    }

    const tableGrid = document.querySelector(".kisiler-table-grid");
    if (!tableGrid) return;
    tableGrid.innerHTML = "";

    const col = (id, name, minWidth) => ({
        id,
        name: html(`<div style="margin-top:3px;">${name}</div>`),
        minWidth,
        formatter: (cell) => ellipsisCell(cell),
    });

    kisilerGrid = new Grid({
        columns: [
            col("tc", "T.C. Kimlik No", "150px"),
            col("first_name", "Ad", "130px"),
            col("last_name", "Soyad", "130px"),
            col("departman_adi", "Departman", "160px"),
            col("unvan", "Ünvan", "140px"),
            {
                id: "is_active",
                name: html('<div style="margin-top:3px;">Durum</div>'),
                sort: false,
                formatter: (cell) => {
                    const aktif = cell === 1 || cell === true;
                    return html(
                        aktif
                            ? `<span class="kisiler-durum-badge kisiler-durum-badge--aktif">Aktif</span>`
                            : `<span class="kisiler-durum-badge kisiler-durum-badge--pasif">Pasif</span>`
                    );
                },
            },
            {
                id: "aksiyon",
                name: html('<div style="cursor:default; text-align:center;">İşlemler</div>'),
                sort: false,
                formatter: (_cell, row) => {
                    const tc = row?.cells?.[0]?.data ?? "";
                    const label = `${row?.cells?.[1]?.data ?? ""} ${row?.cells?.[2]?.data ?? ""}`.trim();
                    const isActive = row?.cells?.[5]?.data;
                    const toggleIcon = isActive ? "person_off" : "person_add";
                    const toggleTitle = isActive ? "Pasife Al" : "Aktife Al";
                    const isSelf = tc && tc === uiState.currentUser?.kisiTc;
                    return html(`
                        <div class="grid-actions">
                            <button class="grid-action-btn kisiler-grid-action-btn--view" data-tc="${tc}" title="Görüntüle">
                                <span class="material-icons">visibility</span>
                            </button>
                            ${isSelf ? `<span class="grid-self-badge" title="Bu sizsiniz"><span class="material-icons">person</span></span>` : `
                            <button class="grid-action-btn kisiler-grid-action-btn--edit" data-tc="${tc}" title="Düzenle">
                                <span class="material-icons">edit</span>
                            </button>
                            <button class="grid-action-btn kisiler-grid-action-btn--toggle" data-tc="${tc}" data-label="${label}" data-active="${isActive ? '1' : '0'}" title="${toggleTitle}">
                                <span class="material-icons">${toggleIcon}</span>
                            </button>`}
                        </div>
                    `);
                },
                attributes: {
                    style: "position: sticky; right: 0; padding: 8px 12px; min-width: 100px;",
                },
            },
        ],

        server: {
            url,
            then: (data) => {
                const total = data.count ?? 0;
                const countEl = document.getElementById("kisiler-count");
                if (countEl) countEl.textContent = `${total} kayıt`;
                return (data.items || []).map((row) => [
                    row.tc,
                    row.first_name,
                    row.last_name,
                    row.departman_adi,
                    row.unvan,
                    row.is_active,
                    null,
                ]);
            },
            total: (data) => data.count ?? 0,
        },

        autoWidth: false,
        search: false,
        sort: true,
        pagination: { limit: paginationLimit },
        fixedHeader: false, // ← TEK DEĞİŞİKLİK: true iken header/body için 2 ayrı tablo render edilir, genişlikler uyuşmaz

        style: {
            container: { padding: "0px" },
            footer: { "border-radius": "0px", "box-shadow": "none" },
            header: { padding: "10px 20px" },
            table: { "white-space": "nowrap", "font-size": "11px", "margin-bottom": "3px" },
            th: { padding: "8px 16px" },
            td: { padding: "10px 16px" },
        },
    }).render(tableGrid);

    initKisilerGridFooter();
}

export function rerenderKisilerGridWithLastUrl(paginationLimit) {
    const lastUrl = uiState.kisiler.lastSearchUrl;
    if (!lastUrl) return;
    renderKisilerGrid({ url: lastUrl, paginationLimit });
}

export function clearKisilerGrid() {
    if (kisilerGrid) {
        kisilerGrid.destroy();
        kisilerGrid = null;
    }
    const tableGrid = document.querySelector(".kisiler-table-grid");
    if (tableGrid) tableGrid.innerHTML = "";
    uiState.kisiler.lastSearchUrl = null;
    uiState.kisiler.paginationLimit = 10;
}

function initKisilerGridFooter() {
    requestAnimationFrame(() => {
        const pagination = document.querySelector(".kisiler-table-grid .gridjs-pagination");
        if (!pagination) return;
        if (pagination.querySelector(".kisiler-per-page-container")) return;

        const recordsInfo = document.createElement("div");
        recordsInfo.classList.add("grid-records-info");
        pagination.insertBefore(recordsInfo, pagination.firstChild);

        const perPageContainer = document.createElement("div");
        perPageContainer.classList.add("per-page-container", "kisiler-per-page-container");

        const label = document.createElement("span");
        label.textContent = "Sayfa başı:";

        const select = document.createElement("select");
        select.classList.add("kisiler-per-page");

        ["5", "10", "25", "50", "100"].forEach((val) => {
            const opt = document.createElement("option");
            opt.value = val;
            opt.textContent = val;
            if (val === String(uiState.kisiler.paginationLimit)) opt.selected = true;
            select.appendChild(opt);
        });

        perPageContainer.appendChild(label);
        perPageContainer.appendChild(select);
        pagination.appendChild(perPageContainer);
    });
}
